import { useTranslation } from 'react-i18next'
import type { FeedItem as FeedItemType } from '@olimhub/shared'
import { FeedItem } from './FeedItem'
import { FeedCTA } from '@/components/common/FeedCTA'
import { PostCardSkeleton } from '@/components/common/Skeleton'
import { getFeedItemId } from '@/lib/feedItem'

interface FeedListProps {
  items: FeedItemType[]
  hasMore: boolean
  isLoading: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
  readOnly?: boolean
  showCTA?: boolean
  emptyNode?: React.ReactNode
  // Suppress the "Posted in [Group]" attribution band — redundant when
  // already viewing the group's own feed (GroupPage passes this).
  hideGroupAttribution?: boolean
  viewerIsGroupAdmin?: boolean
}

export function FeedList({
  items,
  hasMore,
  isLoading,
  isFetchingMore,
  onLoadMore,
  readOnly,
  showCTA,
  emptyNode,
  hideGroupAttribution,
  viewerIsGroupAdmin,
}: FeedListProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px' }}>
        <PostCardSkeleton />
        <PostCardSkeleton />
        <PostCardSkeleton />
      </div>
    )
  }

  if (items.length === 0) {
    if (emptyNode) return <>{emptyNode}</>
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '48px 24px',
          fontFamily: 'var(--font-sans)',
          color: 'var(--text-faint)',
          fontSize: 'var(--fs-sm)',
        }}
      >
        {t('feed.empty_local')}
      </div>
    )
  }

  // Build list with optional CTAs inserted every 5 posts
  const listElements: React.ReactNode[] = []
  let postCount = 0

  items.forEach((item, idx) => {
    listElements.push(
      <FeedItem
        key={item.kind === 'post' ? `post-${item.data.id}` : `${item.kind}-${getFeedItemId(item)}-${idx}`}
        item={item}
        readOnly={readOnly}
        hideGroupAttribution={hideGroupAttribution}
        viewerIsGroupAdmin={viewerIsGroupAdmin}
      />
    )

    if (item.kind === 'post') {
      postCount++
      if (showCTA && postCount % 5 === 0) {
        listElements.push(<FeedCTA key={`cta-${postCount}`} />)
      }
    }
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px' }}>
      {listElements}

      {hasMore && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={isFetchingMore}
          style={{
            width: '100%',
            padding: '12px 0',
            marginTop: 8,
            border: '1.5px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface)',
            color: 'var(--text-muted)',
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 'var(--fs-sm)',
            cursor: isFetchingMore ? 'not-allowed' : 'pointer',
            opacity: isFetchingMore ? 0.6 : 1,
          }}
        >
          {isFetchingMore ? t('feed.loading_more') : t('feed.load_more')}
        </button>
      )}

      {!hasMore && items.length > 0 && (
        <p
          style={{
            textAlign: 'center',
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-faint)',
            fontFamily: 'var(--font-sans)',
            padding: '16px 0 8px',
            margin: 0,
          }}
        >
          {t('feed.end')}
        </p>
      )}
    </div>
  )
}
